import { Grid, Stack } from '@mui/material';
import React from 'react';

// eslint-disable-next-line @nx/enforce-module-boundaries
import { Typography, Box, Card, CircularProgress, Divider } from '@occc-app/design-system';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { MetricInfo } from '@occc-app/routes/Survey/SurveyDetailsPage/MetricInfo';

import { MetricsChart } from './metrics-chart.component';
import { MetricsChartDataItem } from './metrics-chart.types';
import { metricsMessages } from './metrics.messages';
import { formatMetricsValue } from './metrics.utils';

export type MetricsValues = {
  uniqueCodesShared: number;
  newMembersReferred: number;
  newMembersPerReferral: number;
  completedReferrals: number;
  senderAwardsGranted: number;
  senderAwardsRedeemed: number;
  receiverAwardsGranted: number;
  receiverAwardsRedeemed: number;
  senderPointsGranted: number;
  receiverPointsGranted: number;
  chartData: MetricsChartDataItem[];
};

export type MetricsProps = {
  isLoading: boolean;
  isLoadingFailed: boolean;
  values: MetricsValues;
};

export const Metrics = (props: MetricsProps) => {
  const { isLoading, values, isLoadingFailed } = props;

  if (isLoadingFailed) {
    return (
      <Box component={Card} p={3}>
        <Typography color='error'>{metricsMessages.loadingFailedErrorMessage}</Typography>
      </Box>
    );
  }

  return (
    <Box component={Card} p={3}>
      <Stack direction='row' justifyContent='space-between'>
        <Typography fontWeight='bold'>{metricsMessages.title}</Typography>
        {isLoading && <CircularProgress size={18} />}
      </Stack>
      <Divider sx={{ mx: -3, my: 3 }} />
      <Grid container columnSpacing={3} rowSpacing={3}>
        <Grid item xs={6} sm={3}>
          <MetricInfo
            value={formatMetricsValue(values.uniqueCodesShared)}
            title='Senders'
            tooltipText='Count of unique members who shared their code'
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <MetricInfo
            value={formatMetricsValue(values.newMembersReferred)}
            title='New Members Referred'
            tooltipText='Members who submitted a code at sign up'
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <MetricInfo
            value={formatMetricsValue(values.newMembersPerReferral)}
            title='New Members per Referral'
            tooltipText='Average number of new members per referral code'
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <MetricInfo
            value={formatMetricsValue(values.completedReferrals)}
            title='Completed Referrals'
            tooltipText='Count of receivers who completed their qualifying events'
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <MetricInfo
            value={formatMetricsValue(values.senderAwardsGranted)}
            title='Sender Awards Granted'
            tooltipText='Number of awards granted to code senders'
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <MetricInfo
            value={formatMetricsValue(values.senderAwardsRedeemed)}
            title='Sender Awards Redeemed'
            tooltipText='Number of awards code senders redeemed'
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <MetricInfo
            value={formatMetricsValue(values.receiverAwardsGranted)}
            title='Receiver Awards Granted'
            tooltipText='Number of awards granted to code receivers'
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <MetricInfo
            value={formatMetricsValue(values.receiverAwardsRedeemed)}
            title='Receiver Awards Redeemed'
            tooltipText='Number of awards code receivers redeemed'
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <MetricInfo
            value={formatMetricsValue(values.senderPointsGranted)}
            title='Sender Points Granted'
            tooltipText='Number of points granted to code senders'
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <MetricInfo
            value={formatMetricsValue(values.receiverPointsGranted)}
            title='Receiver Points Granted'
            tooltipText='Number of points granted to code receivers'
          />
        </Grid>
      </Grid>
      <Box mt={3}>
        <MetricsChart data={values.chartData} />
      </Box>
    </Box>
  );
};
